/**
 * Centralized Route Path Constants
 *
 * This file contains all route path definitions used throughout the application.
 * By centralizing these paths, we ensure consistency and make it easier to update routes.
 *
 * Structure:
 * - routeSegments.ts: Path segment strings and ServiceType definition
 * - routeService.ts: Service-specific logic (A2/R2 switching)
 * - routes.ts (this file): Route definitions and helpers
 *
 * Usage:
 * - Import route constants: import { ADMIN_ROUTES, TENANT_ROUTES } from '@/constants/routes'
 * - Use in navigation: navigate(ADMIN_ROUTES.CLINICS.LIST(site))
 * - Use in links: <Link to={TENANT_ROUTES.PATIENTS.EDIT(tenant)}>Edit</Link>
 */

// Re-export everything from sub-modules for convenience
export {
  DEFAULT_SITES,
  ROUTE_SEGMENTS,
  EXTERNAL_URL_SEGMENTS,
  type ServiceType,
} from './routeSegments';

export {
  SERVICE_ROUTE_EXCEPTIONS,
  isRouteAvailableInService,
  isTenantPathAvailableInService,
  getPathAfterServiceSwitch,
} from './routeService';

// Import for use in this file
import { ROUTE_SEGMENTS, EXTERNAL_URL_SEGMENTS, DEFAULT_SITES } from './routeSegments';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Creates a resource object with CRUD operations
 * @param basePath Function that returns the base path
 * @param options Object specifying which CRUD operations are available
 */
interface ResourceOptions {
  hasCreate?: boolean;
  hasEdit?: boolean;
  hasDetail?: boolean;
  hasCopy?: boolean;
}

const createResource = (basePath: (param: string) => string, options: ResourceOptions = {}) => {
  const resource: Record<string, any> = {
    LIST: basePath,
  };

  if (options.hasCreate !== false) {
    resource.CREATE = (param: string) => `${basePath(param)}/${ROUTE_SEGMENTS.CREATE}`;
  }

  if (options.hasEdit !== false) {
    resource.EDIT = (param: string, id: string | number) =>
      `${basePath(param)}/${ROUTE_SEGMENTS.EDIT}/${id}`;
  }

  if (options.hasDetail !== false) {
    resource.DETAIL = (param: string, id: string | number) => `${basePath(param)}/${id}`;
  }

  if (options.hasCopy) {
    resource.COPY = (param: string, id: string | number) =>
      `${basePath(param)}/${id}/${ROUTE_SEGMENTS.COPY}`;
  }

  return resource;
};

/**
 * Creates a simple list-only resource object
 * @param basePath Function that returns the base path
 */
const createListResource = (basePath: (param: string) => string) => {
  return {
    LIST: basePath,
  };
};

// ============================================================================
// Authentication Routes
// ============================================================================

export const AUTH_ROUTES = {
  ADMIN_LOGIN: (site: string) => `/${site}/${ROUTE_SEGMENTS.ADMIN}/${ROUTE_SEGMENTS.LOGIN}`,
  LOGIN: (site: string) => `/${site}/${ROUTE_SEGMENTS.LOGIN}`,
  FORGOT_PASSWORD: (site: string) => `/${site}/${ROUTE_SEGMENTS.FORGOT_PASSWORD}`,
  RESET_PASSWORD: (site: string) => `/${site}/${ROUTE_SEGMENTS.RESET_PASSWORD}`,
  PASSWORD_RESET_TOKEN: (site: string, token: string) =>
    `/${site}/${ROUTE_SEGMENTS.PASSWORD}/${ROUTE_SEGMENTS.RESET}/${token}`,
  DEFAULT_REDIRECT: () => `/${DEFAULT_SITES.MEDIX}/${ROUTE_SEGMENTS.ADMIN}/${ROUTE_SEGMENTS.LOGIN}`,
  ROOT: () => '/',
} as const;

// ============================================================================
// Admin Portal Routes - /:site/admin/*
// ============================================================================

export const ADMIN_ROUTES = {
  ROOT: (site: string) => `/${site}/${ROUTE_SEGMENTS.ADMIN}`,
  DASHBOARD: (site: string) => `/${site}/${ROUTE_SEGMENTS.ADMIN}`,

  CLINICS: createResource(
    (site: string) => `/${site}/${ROUTE_SEGMENTS.ADMIN}/${ROUTE_SEGMENTS.CLINICS}`,
    {
      hasCreate: true,
      hasEdit: true,
      hasDetail: true,
      hasCopy: false,
    }
  ),

  CLINIC_INFORMATIONS: createResource(
    (site: string) => `/${site}/${ROUTE_SEGMENTS.ADMIN}/${ROUTE_SEGMENTS.CLINIC_INFORMATIONS}`,
    {
      hasCreate: true,
      hasEdit: true,
      hasCopy: true,
    }
  ),

  CLINIC_LOCATIONS: {
    ...(createResource(
      (site: string) => `/${site}/${ROUTE_SEGMENTS.ADMIN}/${ROUTE_SEGMENTS.CLINIC_LOCATIONS}`
    ) as any),
    CREATE_MANAGER: (site: string, id: string | number) =>
      `/${site}/${ROUTE_SEGMENTS.ADMIN}/${ROUTE_SEGMENTS.CLINIC_LOCATIONS}/${id}/${ROUTE_SEGMENTS.CLINIC_LOCATION_MANAGERS}/${ROUTE_SEGMENTS.CREATE}`,
  } as any,

  CLINIC_USERS: createResource(
    (site: string) => `/${site}/${ROUTE_SEGMENTS.ADMIN}/${ROUTE_SEGMENTS.CLINIC_USERS}`,
    {
      hasCreate: true,
      hasEdit: true,
      hasDetail: false,
      hasCopy: false,
    }
  ),

  MASSAGERS: {
    ...(createResource(
      (site: string) => `/${site}/${ROUTE_SEGMENTS.ADMIN}/${ROUTE_SEGMENTS.MASSAGERS}`,
      {
        hasCreate: true,
        hasEdit: true,
        hasCopy: true,
      }
    ) as Record<string, any>),
    CREATE: (site: string, clinicId?: string | number) =>
      `/${site}/${ROUTE_SEGMENTS.ADMIN}/${ROUTE_SEGMENTS.MASSAGERS}/${ROUTE_SEGMENTS.CREATE}${clinicId ? `?clinic_id=${clinicId}` : ''}`,
  } as Record<string, any>,

  INSURERS: createResource(
    (site: string) => `/${site}/${ROUTE_SEGMENTS.ADMIN}/${ROUTE_SEGMENTS.INSURERS}`,
    {
      hasCreate: false,
      hasEdit: false,
      hasDetail: true,
      hasCopy: false,
    }
  ),

  SPECIAL_CALCULATION_COSTS: {
    DETAIL: (site: string, id: string | number) =>
      `/${site}/${ROUTE_SEGMENTS.ADMIN}/${ROUTE_SEGMENTS.SPECIAL_CALCULATION_COSTS}/${id}`,
  },

  // Simple routes (no CRUD operations)
  BANK_TRANSFERS: (site: string) =>
    `/${site}/${ROUTE_SEGMENTS.ADMIN}/${ROUTE_SEGMENTS.BANK_TRANSFERS}`,
  REZEPT_HISTORIES: (site: string) =>
    `/${site}/${ROUTE_SEGMENTS.ADMIN}/${ROUTE_SEGMENTS.REZEPT_HISTORIES}`,
  INFORMATIONS: createResource(
    (site: string) => `/${site}/${ROUTE_SEGMENTS.ADMIN}/${ROUTE_SEGMENTS.INFORMATIONS}`,
    {
      hasCreate: true,
      hasEdit: true,
      hasDetail: true,
      hasCopy: false,
    }
  ),
  DELETE_CLINICS: (site: string) =>
    `/${site}/${ROUTE_SEGMENTS.ADMIN}/${ROUTE_SEGMENTS.DELETE_CLINICS}`,
  DESIGN_TEMPLATE: (site: string) =>
    `/${site}/${ROUTE_SEGMENTS.ADMIN}/${ROUTE_SEGMENTS.DESIGN_TEMPLATE}`,
  SERVER_ERROR: (site: string) => `/${site}/${ROUTE_SEGMENTS.ADMIN}/${ROUTE_SEGMENTS.SERVER_ERROR}`,
};

// ============================================================================
// Tenant Portal Routes - /:tenant/*
// ============================================================================

export const TENANT_ROUTES = {
  ROOT: (tenant: string) => `/${tenant}`,

  // Patients (main feature with nested routes)
  PATIENTS: {
    ROOT: (tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.PATIENTS}`,
    LIST: (tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.PATIENTS}`,
    EDIT: (tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.PATIENTS}/${ROUTE_SEGMENTS.EDIT}`,
    EDIT_BY_ID: (tenant: string, id: string | number) =>
      `/${tenant}/${ROUTE_SEGMENTS.PATIENTS}/${ROUTE_SEGMENTS.EDIT}/${id}`,
    DETAIL: (tenant: string, id: string | number) => `/${tenant}/${ROUTE_SEGMENTS.PATIENTS}/${id}`,
    HISTORIES: (tenant: string, id: string | number) =>
      `/${tenant}/${ROUTE_SEGMENTS.PATIENTS}/${id}/${ROUTE_SEGMENTS.HISTORIES}`,
    DM_INFO_EDIT: (tenant: string, id: string | number) =>
      `/${tenant}/${ROUTE_SEGMENTS.PATIENTS}/${id}/${ROUTE_SEGMENTS.DM_INFO}/${ROUTE_SEGMENTS.EDIT}`,
    LONG_TERM_FREQUENTS: (tenant: string, id: string | number) =>
      `/${tenant}/${ROUTE_SEGMENTS.PATIENTS}/${id}/${ROUTE_SEGMENTS.LONG_TERM_FREQUENTS}`,
    CHECKIN_MAILS: (tenant: string, id: string | number) =>
      `/${tenant}/${ROUTE_SEGMENTS.PATIENTS}/${id}/${ROUTE_SEGMENTS.CHECKIN_MAILS}`,
    TREATMENT_REPORTS: (tenant: string, id: string | number) =>
      `/${tenant}/${ROUTE_SEGMENTS.TREATMENT_REPORTS}/${id}/${ROUTE_SEGMENTS.LIST}`,
    CERTIFICATION: (tenant: string) =>
      `/${tenant}/${ROUTE_SEGMENTS.PATIENTS}/${ROUTE_SEGMENTS.CERTIFICATION}`,
    CONTINUATION: (tenant: string) =>
      `/${tenant}/${ROUTE_SEGMENTS.PATIENTS}/${ROUTE_SEGMENTS.CONTINUATION}`,
    SEARCH: (tenant: string) =>
      `/${tenant}/${ROUTE_SEGMENTS.PATIENTS}/${ROUTE_SEGMENTS.SEARCH_PATIENTS}`,
  },

  // Consents
  CONSENTS: {
    ROOT: (tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.CONSENTS}`,
    LIST: (tenant: string, patientId: string | number) =>
      `/${tenant}/${ROUTE_SEGMENTS.CONSENTS}/${patientId}/${ROUTE_SEGMENTS.LIST}`,
    CREATE: (tenant: string, patientId: string | number) =>
      `/${tenant}/${ROUTE_SEGMENTS.CONSENTS}/${patientId}/${ROUTE_SEGMENTS.CREATE}`,
    EDIT: (tenant: string, patientId: string | number, id: string | number) =>
      `/${tenant}/${ROUTE_SEGMENTS.CONSENTS}/${patientId}/${ROUTE_SEGMENTS.EDIT}/${id}`,
    TREATMENT_PERIOD: (tenant: string) =>
      `/${tenant}/${ROUTE_SEGMENTS.CONSENTS}/${ROUTE_SEGMENTS.TREATMENT_PERIOD}`,
    TREATMENT_RECORD_PRINT: (tenant: string, patientId: string | number, consentId: string | number) =>
      `/${tenant}/${ROUTE_SEGMENTS.CONSENTS}/${patientId}/${consentId}/${ROUTE_SEGMENTS.TREATMENT_RECORD_PRINT}`,
  },

  // Patient Addresses (訪問住所)
  PATIENT_ADDRESSES: {
    LIST: (tenant: string, patientId: string | number) =>
      `/${tenant}/patient_addresses/${patientId}/${ROUTE_SEGMENTS.LIST}`,
    CREATE: (tenant: string, patientId: string | number) =>
      `/${tenant}/patient_addresses/${patientId}/${ROUTE_SEGMENTS.CREATE}`,
    EDIT: (tenant: string, patientId: string | number, id: string | number) =>
      `/${tenant}/patient_addresses/${patientId}/${ROUTE_SEGMENTS.EDIT}/${id}`,
  },

  // Long Term Frequents (16回以上施術理由)
  LONG_TERM_FREQUENTS: {
    LIST: (tenant: string, patientId: string | number) =>
      `/${tenant}/long_term_frequents/${patientId}/${ROUTE_SEGMENTS.LIST}`,
    CREATE: (tenant: string, patientId: string | number) =>
      `/${tenant}/long_term_frequents/${patientId}/${ROUTE_SEGMENTS.CREATE}`,
    EDIT: (tenant: string, patientId: string | number, id: string | number) =>
      `/${tenant}/long_term_frequents/${patientId}/${ROUTE_SEGMENTS.EDIT}/${id}`,
  },

  // Checkin Mails (チェックイン通知)
  CHECKIN_MAILS: {
    LIST: (tenant: string, patientId: string | number) =>
      `/${tenant}/checkin_mails/${patientId}/${ROUTE_SEGMENTS.LIST}`,
    CREATE: (tenant: string, patientId: string | number) =>
      `/${tenant}/checkin_mails/${patientId}/${ROUTE_SEGMENTS.CREATE}`,
    EDIT: (tenant: string, patientId: string | number, id: string | number) =>
      `/${tenant}/checkin_mails/${patientId}/${ROUTE_SEGMENTS.EDIT}/${id}`,
  },

  R_CONSENTS: {
    LIST: (tenant: string, patientId: string | number) =>
      `/${tenant}/${ROUTE_SEGMENTS.R_CONSENTS}/${patientId}/${ROUTE_SEGMENTS.LIST}`,
  },

  REZEPT_APPLICATION_COUNTS: {
    LIST: (tenant: string, patientId: string | number) =>
      `/${tenant}/${ROUTE_SEGMENTS.REZEPT_APPLICATION_COUNTS}/${patientId}/${ROUTE_SEGMENTS.LIST}`,
  },

  /**
   * 通知センター — the clinic portal's own copy of the admin screen (1 row = 1 press).
   *
   * Underscore, not a hyphen: the tenant router registers `notification_center` and has no hyphen alias,
   * unlike the admin router. A hyphen here lands on the catch-all 404.
   */
  NOTIFICATION_CENTER: (tenant: string) => `/${tenant}/notification_center`,

  // Rezepts
  REZEPTS: {
    ROOT: (tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.REZEPTS}`,
    RETURN_INIT: (tenant: string) =>
      `/${tenant}/${ROUTE_SEGMENTS.REZEPTS}/${ROUTE_SEGMENTS.RETURN_INIT}`,
    STATISTICAL_INIT: (tenant: string) =>
      `/${tenant}/${ROUTE_SEGMENTS.REZEPTS}/${ROUTE_SEGMENTS.STATISTICAL_INIT}`,
    VISIT_CLINIC_INVOICES_INIT: (tenant: string) =>
      `/${tenant}/${ROUTE_SEGMENTS.REZEPTS}/${ROUTE_SEGMENTS.VISIT_CLINIC_INVOICES_INIT}`,
    INVOICES_INIT: (tenant: string) =>
      `/${tenant}/${ROUTE_SEGMENTS.REZEPTS}/${ROUTE_SEGMENTS.INVOICES_INIT}`,
    INIT_MASSAGERS: (tenant: string) =>
      `/${tenant}/${ROUTE_SEGMENTS.REZEPTS}/${ROUTE_SEGMENTS.INIT_MASSAGERS}`,
  },

  // Visits
  VISITS: {
    ROOT: (tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.VISITS}`,
    SCHEDULE: (tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.VISITS}/${ROUTE_SEGMENTS.SCHEDULE}`,
    PATIENT_INIT: (tenant: string) =>
      `/${tenant}/${ROUTE_SEGMENTS.VISITS}/${ROUTE_SEGMENTS.PATIENT_INIT}`,
    MASSAGER_INIT: (tenant: string) =>
      `/${tenant}/${ROUTE_SEGMENTS.VISITS}/${ROUTE_SEGMENTS.MASSAGER_INIT}`,
    VISITPATIENT_INIT: (tenant: string) =>
      `/${tenant}/${ROUTE_SEGMENTS.VISITS}/${ROUTE_SEGMENTS.VISITPATIENT_INIT}`,
  },

  // Master Maintenance
  CLINIC_INFORMATIONS: {
    ROOT: (tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.CLINIC_INFORMATIONS}`,
    LIST: (tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.CLINIC_INFORMATIONS}`,
  },

  CLINIC_LOCATIONS: {
    ROOT: (tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.CLINIC_LOCATIONS}`,
    LIST: (tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.CLINIC_LOCATIONS}`,
    DETAIL: (tenant: string, id: string | number) =>
      `/${tenant}/${ROUTE_SEGMENTS.CLINIC_LOCATIONS}/${id}`,
  },

  INSURERS: createListResource((tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.INSURERS}`),
  INSURER_ATTENTIONS: createListResource(
    (tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.INSURER_ATTENTIONS}`
  ),
  MASSAGERS: createListResource((tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.MASSAGERS}`),
  HOSPITALS: createListResource((tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.HOSPITALS}`),
  FREE_MEDICAL: createListResource((tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.FREE_MEDICAL}`),
  MEDICAL_EXPENSE_PRICES: createListResource(
    (tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.MEDICAL_EXPENSE_PRICES}`
  ),
  DOCUMENT_TEMPLATES: createListResource(
    (tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.DOCUMENT_TEMPLATES}`
  ),

  // Sheets / Receipt Paper
  SHEETS: {
    ROOT: (tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.SHEETS}`,
    ORDER: (tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.SHEETS}/${ROUTE_SEGMENTS.ORDER}`,
    ORDER_HISTORIES: (tenant: string) =>
      `/${tenant}/${ROUTE_SEGMENTS.SHEETS}/${ROUTE_SEGMENTS.ORDER_HISTORIES}`,
    HISTORIES: (tenant: string) =>
      `/${tenant}/${ROUTE_SEGMENTS.SHEETS}/${ROUTE_SEGMENTS.HISTORIES}`,
  },

  // Treatment Records
  TREATMENT_RECORDS: {
    LIST: (tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.TREATMENT_RECORDS}`,
  },

  // Other Routes
  DESIGN_TEMPLATE: (tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.DESIGN_TEMPLATE}`,
  CONTACT_EDIT: (tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.CONTACT}/${ROUTE_SEGMENTS.EDIT}`,
  RECEIPT_PAPER_ORDER: (tenant: string) => `/${tenant}/${ROUTE_SEGMENTS.RECEIPT_PAPER_ORDER}`,
} as const;

// ============================================================================
// External URLs & Manual Links
// ============================================================================

export const EXTERNAL_URLS = {
  MANUAL: {
    BASE: `/${EXTERNAL_URL_SEGMENTS.STORAGE}/${EXTERNAL_URL_SEGMENTS.MANUAL}`,
    PATIENTS_EDIT: `/${EXTERNAL_URL_SEGMENTS.STORAGE}/${EXTERNAL_URL_SEGMENTS.MANUAL}/${ROUTE_SEGMENTS.PATIENTS}/${ROUTE_SEGMENTS.EDIT}/`,
    PATIENTS_HISTORIES: `/${EXTERNAL_URL_SEGMENTS.STORAGE}/${EXTERNAL_URL_SEGMENTS.MANUAL}/${ROUTE_SEGMENTS.PATIENTS}/${ROUTE_SEGMENTS.HISTORIES}/`,
    PATIENTS_DMINFO_EDIT: `/${EXTERNAL_URL_SEGMENTS.STORAGE}/${EXTERNAL_URL_SEGMENTS.MANUAL}/${ROUTE_SEGMENTS.PATIENTS}/${ROUTE_SEGMENTS.DM_INFO}/${ROUTE_SEGMENTS.EDIT}/`,
    CLINIC_INFORMATIONS: `/${EXTERNAL_URL_SEGMENTS.STORAGE}/${EXTERNAL_URL_SEGMENTS.MANUAL}/${ROUTE_SEGMENTS.CLINIC_INFORMATIONS}/`,
    INSURERS: `/${EXTERNAL_URL_SEGMENTS.STORAGE}/${EXTERNAL_URL_SEGMENTS.MANUAL}/${ROUTE_SEGMENTS.INSURERS}/`,
    MASSAGERS: `/${EXTERNAL_URL_SEGMENTS.STORAGE}/${EXTERNAL_URL_SEGMENTS.MANUAL}/${ROUTE_SEGMENTS.MASSAGERS}/`,
    HOSPITALS: `/${EXTERNAL_URL_SEGMENTS.STORAGE}/${EXTERNAL_URL_SEGMENTS.MANUAL}/${ROUTE_SEGMENTS.HOSPITALS}/`,
    DOCUMENT_TEMPLATES: `/${EXTERNAL_URL_SEGMENTS.STORAGE}/${EXTERNAL_URL_SEGMENTS.MANUAL}/${ROUTE_SEGMENTS.DOCUMENT_TEMPLATES}/`,
    SHEETS_ORDER: `/${EXTERNAL_URL_SEGMENTS.STORAGE}/${EXTERNAL_URL_SEGMENTS.MANUAL}/${ROUTE_SEGMENTS.SHEETS}/${ROUTE_SEGMENTS.ORDER}/`,
  },
  PDF_MANUALS: {
    A2_INTEGRATED: `/${DEFAULT_SITES.MEDIX}/${EXTERNAL_URL_SEGMENTS.MANUAL}/${EXTERNAL_URL_SEGMENTS.R2_MANUAL}/★A2マニュアル統合版（20230529）.pdf`,
    A2_CHECKIN: `/${DEFAULT_SITES.MEDIX}/${EXTERNAL_URL_SEGMENTS.MANUAL}/${EXTERNAL_URL_SEGMENTS.R2_MANUAL}/A2マニュアル_42_チェックイン機能.pdf`,
    R2_INTEGRATED: `/${DEFAULT_SITES.MEDIX}/${EXTERNAL_URL_SEGMENTS.MANUAL}/${EXTERNAL_URL_SEGMENTS.R2_MANUAL}/★R2マニュアル統合版（20220606）.pdf`,
    R2_CHECKIN: `/${DEFAULT_SITES.MEDIX}/${EXTERNAL_URL_SEGMENTS.MANUAL}/${EXTERNAL_URL_SEGMENTS.R2_MANUAL}/R2マニュアル_34_チェックイン機能.pdf`,
  },
} as const;
